<template>
  <AuthLayout>
    <template #header>
      <div v-if="emailSuccess" class="flex flex-col items-center">
        <div class="mb-6">
          <div class="flex gap-4">
            <Icon name="MailCheck" class="h-[40px] w-[40px] text-success" />
            <h1 class="text-[32px] font-semibold">Instructions Sent!</h1>
          </div>
          <p class="text-strong text-lg leading-5">
            If your email was found in our system, you will receive an email for reset instructions.
          </p>
        </div>
        <Button @click="router.push('/sign-in')" class="w-full" variant="primary-gray">
          <template #label>Back to Sign In</template>
        </Button>
      </div>
      <div v-else>
        <Button @click="handleBack" variant="paper-primary" size="sm" class="hidden lg:flex mb-8">
          <template #icon>
            <Icon name="ChevronLeft" class="h-4 w-4" />
          </template>
        </Button>
        <h1 class="text-[32px] font-semibold">Forgot your password?</h1>
        <p class="text-strong text-lg leading-5">
          Please enter your email and we will send instructions on how to change your password.
        </p>
      </div>
    </template>

    <div class="w-full">
      <div v-if="!emailSuccess" class="w-full">
        <div class="flex flex-col mb-8 w-full">
          <BaseInput
            v-model="forgotPasswordEmail"
            label="Email"
            field_type="string"
            field_name="email"
            is_required
            placeholder="Type Here"
            size="large"
            variant="secondary"
            @keydown.enter="handleSubmitForgotPasswordEmail"
          />
          <div v-if="submitError" class="mt-3 ml-3 font-inter text-error text-sm flex items-center">
            <Icon
              name="Asterisk"
              class="mr-2 h-[14px] w-[14px] min-w-[14px] -skew-x-12 stroke-error"
            />
            {{ submitError }}
          </div>
        </div>
        <div class="flex flex-col items-center w-full gap-4">
          <Button
            @click="handleSubmitForgotPasswordEmail"
            @keydown.enter="handleSubmitForgotPasswordEmail"
            variant="primary-grey"
            class="w-full"
          >
            <template v-if="is_loading" #label>
              <IconPulseSpinner class="h-6 w-6" />
            </template>
            <template #label>Submit</template>
          </Button>
        </div>
      </div>
      <!-- Turnstile widget -->
      <div :class="emailSuccess ? '' : 'mt-8'" data-theme="light" id="turnstile"></div>
    </div>
  </AuthLayout>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";

import { useAuthStore, useSchemaStore } from "@/stores";
import { useAnalytics } from "@/composables/useAnalytics";
import { BaseInput, Button, Icon } from "@/components";
import { IconPulseSpinner } from "@/components/icons";
import { useTurnstile } from "@/composables/useTurnstile";
import { isValidEmail } from "@/utilities";
import AuthLayout from "./AuthLayout.vue";

// Turnstile composable - handles init, render, cleanup automatically
const { resetTurnstile } = useTurnstile();

const router = useRouter();
const authStore = useAuthStore();
const schemaStore = useSchemaStore();
const { passwordResetRequested } = useAnalytics();

const forgotPasswordEmail = ref("");
const submitError = ref("");
const emailSuccess = ref(false);
const is_loading = ref(false);

onMounted(() => {
  window.scrollTo(0, 0);
});

const authSchema = [
  {
    field_name: "email",
    field_type: "string",
    is_required: true,
    label: "Email",
  },
];

async function handleSubmitForgotPasswordEmail() {
  try {
    const result = schemaStore.validateFields(
      { email: forgotPasswordEmail.value },
      authSchema,
      null,
      true
    );

    if (!result.proceed) {
      return;
    }
    if (!authStore.is_turnstile_valid) {
      submitError.value = "Please complete the security check to confirm that you are a human.";
      return;
    }

    is_loading.value = true;
    submitError.value = "";
    const emailSentResponse = await authStore.sendPasswordResetEmail(forgotPasswordEmail.value);
    if (!emailSentResponse) {
      submitError.value =
        "Error sending password reset email. Please ensure your email address is correct and try again.";
    } else {
      emailSuccess.value = true;
      passwordResetRequested();
    }
  } catch (err) {
    submitError.value =
      "Error sending password reset email. Please ensure your email address is correct and try again.";
  } finally {
    is_loading.value = false;
  }
}

function handleBack() {
  router.push("/sign-in");
}
</script>
